import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Heart, Briefcase, Sparkles, Sun, TrendingUp, Calendar, Clock, Shield, FileText, Check, Eye, Target, AlertTriangle, Compass, Users, Lightbulb, BarChart3, Zap, RefreshCw, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { ServicePurchaseSection } from '../components/ServicePurchaseSection';
interface SolarReturnPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-27156">
      {children}
    </motion.section>;
}
export function SolarReturnPage({
  onNavigate
}: SolarReturnPageProps) {
  const ctaRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const scrollToCta = () => {
    ctaRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const scrollToPricing = () => {
    pricingRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-27157">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 sm:pt-8 pb-14 sm:pb-24 lg:pb-32" data-id="element-27158">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-amber-500/12 rounded-full blur-3xl pointer-events-none" data-id="element-27159" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-27160" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-27161">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Соляр'
        }]} className="mb-6 sm:mb-12 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-27162" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center" data-id="element-27163">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-27164">
              <motion.h1 variants={staggerItem} className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-5 leading-[1.15]" data-id="element-27165">
                Ваш личный астрологический прогноз на следующий год
              </motion.h1>

              <motion.p variants={staggerItem} className="text-sm sm:text-base md:text-lg text-slate-300 mb-6 sm:mb-8 leading-relaxed" data-id="element-27166">
                Соляр показывает главные события, возможности и задачи, которые
                могут проявиться в течение года после вашего дня рождения.
              </motion.p>

              <motion.div variants={staggerItem} className="mb-6 sm:mb-10" data-id="element-27167">
                <Button size="lg" onClick={scrollToCta} className="text-base sm:text-lg px-6 sm:px-8 shadow-lg shadow-amber-500/20 bg-amber-500 hover:bg-amber-600" data-id="element-27168">
                  Рассчитать мой соляр
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-27169" />
                </Button>
              </motion.div>

              <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-27170">
                {[{
                icon: <BarChart3 className="w-4 h-4" data-id="element-27171" />,
                text: 'Прогноз ключевых событий года'
              }, {
                icon: <Clock className="w-4 h-4" data-id="element-27172" />,
                text: 'Анализ важных жизненных периодов'
              }, {
                icon: <Lightbulb className="w-4 h-4" data-id="element-27173" />,
                text: 'Рекомендации для развития'
              }, {
                icon: <Shield className="w-4 h-4" data-id="element-27174" />,
                text: 'Понимание возможностей и рисков'
              }].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-27175">
                    <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-300 flex-shrink-0" data-id="element-27176">
                      {item.icon}
                    </div>
                    {item.text}
                  </div>)}
              </motion.div>
            </motion.div>

            {/* Illustration — Solar Return concept */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.2
          }} className="hidden lg:flex justify-center relative" data-id="element-27177">
              <div className="w-[420px] h-[420px] rounded-full border border-white/10 flex items-center justify-center relative" data-id="element-27178">
                {/* Orbital rings */}
                <div className="absolute inset-0 rounded-full border border-amber-500/20 animate-[spin_50s_linear_infinite]" data-id="element-27179" />
                <div className="absolute inset-6 rounded-full border border-orange-400/15 animate-[spin_40s_linear_infinite_reverse]" data-id="element-27180" />
                <div className="absolute inset-12 rounded-full border border-amber-300/10 animate-[spin_30s_linear_infinite]" data-id="element-27181" />
                {/* Central glow */}
                <div className="w-52 h-52 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full blur-3xl opacity-25 animate-pulse" data-id="element-27182" />
                {/* Sun icon */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-27183">
                  <Sun className="w-24 h-24 text-amber-300/80" strokeWidth={1} data-id="element-27184" />
                </div>
                {/* Return arrow */}
                <motion.div className="absolute" style={{
                top: '12%',
                right: '18%'
              }} animate={{
                rotate: [0, 360]
              }} transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-27185">
                  <RefreshCw className="w-6 h-6 text-amber-400/50" strokeWidth={1.5} data-id="element-27186" />
                </motion.div>
                {/* Orbiting planets */}
                {[0, 72, 144, 216, 288].map((deg, i) => <div key={i} className="absolute w-2.5 h-2.5 rounded-full" style={{
                background: ['#fbbf24', '#f97316', '#ef4444', '#a78bfa', '#34d399'][i],
                opacity: 0.5,
                top: `${50 + 42 * Math.sin(deg * Math.PI / 180)}%`,
                left: `${50 + 42 * Math.cos(deg * Math.PI / 180)}%`,
                transform: 'translate(-50%, -50%)'
              }} data-id="element-27187" />)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* 2. ЧТО ТАКОЕ СОЛЯР */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-27188">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-27189">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-6 sm:mb-8 text-center" data-id="element-27190">
            Что такое соляр
          </h2>
          <div className="prose prose-lg prose-slate max-w-none text-slate-600 space-y-6" data-id="element-27191">
            <p data-id="element-27192">
              Соляр (солярный гороскоп, Solar Return) — это астрологическая
              карта, построенная на момент <strong data-id="element-27193">возвращения Солнца</strong> в
              то же положение, в котором оно находилось в момент вашего
              рождения. Это происходит каждый год примерно в день вашего
              рождения (иногда с отклонением в один день).
            </p>
            <p data-id="element-27194">
              Солярная карта показывает основные тенденции, события и
              возможности, которые могут проявиться в течение года — от одного
              дня рождения до следующего. По сути, это ваш{' '}
              <strong data-id="element-27195">персональный астрологический прогноз</strong>, который
              обновляется каждый год и каждый раз уникален.
            </p>
            <p data-id="element-27196">
              В отличие от натальной карты, которая описывает вашу личность в
              целом, соляр фокусируется на{' '}
              <strong data-id="element-27197">конкретном годе жизни</strong>: какие сферы будут активны,
              где ждать перемен, когда действовать, а когда лучше выждать.
              Именно поэтому опытные астрологи рекомендуют делать соляр
              ежегодно.
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. ЧТО ПОКАЗЫВАЕТ СОЛЯР */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-27198">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-27199">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-27200">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-27201">
              Что показывает соляр
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-27202">
              Каждый раздел прогноза раскрывает отдельную сферу вашего
              предстоящего года
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto" data-id="element-27203">
            {[{
            icon: <Star className="w-5 h-5" data-id="element-27204" />,
            color: 'bg-amber-50 text-amber-600',
            title: 'Главные события года',
            desc: 'Какие темы будут наиболее важными, на чём сфокусируется ваша энергия и внимание в течение года.'
          }, {
            icon: <Briefcase className="w-5 h-5" data-id="element-27205" />,
            color: 'bg-emerald-50 text-emerald-600',
            title: 'Карьера и деньги',
            desc: 'Периоды карьерного роста, изменения в работе, финансовые возможности и риски.'
          }, {
            icon: <Heart className="w-5 h-5" data-id="element-27206" />,
            color: 'bg-rose-50 text-rose-600',
            title: 'Отношения',
            desc: 'Вероятные изменения в личной жизни, новые знакомства и динамика существующих связей.'
          }, {
            icon: <TrendingUp className="w-5 h-5" data-id="element-27207" />,
            color: 'bg-indigo-50 text-indigo-600',
            title: 'Личностный рост',
            desc: 'Какие уроки и задачи принесёт год, в каких направлениях вы будете развиваться.'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-27208" />,
            color: 'bg-orange-50 text-orange-600',
            title: 'Возможности и риски',
            desc: 'Когда лучше действовать активно, а когда проявлять осторожность и выжидать.'
          }, {
            icon: <Calendar className="w-5 h-5" data-id="element-27209" />,
            color: 'bg-purple-50 text-purple-600',
            title: 'Ключевые периоды',
            desc: 'Помесячный разбор активных тем и наиболее значимых дат в течение года.'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex items-start gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-27210">
                <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-27211">
                  {item.icon}
                </div>
                <div className="min-w-0" data-id="element-27212">
                  <h3 className="text-base font-bold text-slate-900 mb-1" data-id="element-27213">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-27214">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 4. КАК РАССЧИТЫВАЕТСЯ СОЛЯР */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-27215">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-27216">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12" data-id="element-27217">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-27218">
              Как рассчитывается соляр
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-27219">
              Четыре шага от ваших данных до готового прогноза
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto" data-id="element-27220">
            {[{
            step: '01',
            icon: <Sun className="w-5 h-5" data-id="element-27221" />,
            color: 'bg-amber-50 text-amber-600',
            stepColor: 'text-amber-500',
            title: 'Возвращение Солнца',
            desc: 'Система определяет точный момент возвращения Солнца в натальное положение'
          }, {
            step: '02',
            icon: <Star className="w-5 h-5" data-id="element-27222" />,
            color: 'bg-indigo-50 text-indigo-600',
            stepColor: 'text-indigo-500',
            title: 'Солярная карта',
            desc: 'Строится астрологическая карта на момент солярного возвращения'
          }, {
            step: '03',
            icon: <Eye className="w-5 h-5" data-id="element-27223" />,
            color: 'bg-emerald-50 text-emerald-600',
            stepColor: 'text-emerald-500',
            title: 'Анализ карты',
            desc: 'Анализируются положения планет, домов и аспектов солярной карты'
          }, {
            step: '04',
            icon: <FileText className="w-5 h-5" data-id="element-27224" />,
            color: 'bg-rose-50 text-rose-600',
            stepColor: 'text-rose-500',
            title: 'Прогноз на год',
            desc: 'Формируется подробный прогноз на следующий год вашей жизни'
          }].map((item, i) => <motion.div key={i} variants={staggerItem} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="flex items-center gap-4 bg-slate-50 rounded-xl px-5 py-4 border border-slate-100" data-id="element-27225">
                <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-27226">
                  {item.icon}
                </div>
                <div className="min-w-0 flex-1" data-id="element-27227">
                  <div className="flex items-center gap-2 mb-0.5" data-id="element-27228">
                    <span className={`text-xs font-bold ${item.stepColor}`} data-id="element-27229">
                      {item.step}
                    </span>
                    <h3 className="text-base font-bold text-slate-900" data-id="element-27230">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-slate-500 text-sm" data-id="element-27231">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 5. ЧТО ВХОДИТ В ОТЧЁТ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-27232">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-27233">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-27234">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-27235">
              Что входит в отчёт соляра
            </h2>
            <p className="text-base sm:text-lg text-slate-600" data-id="element-27236">
              Подробный документ с полным анализом вашего предстоящего года
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto" data-id="element-27237">
            {[{
            icon: <Sun className="w-5 h-5" data-id="element-27238" />,
            text: 'Общая тема и главный фокус года'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-27239" />,
            text: 'Влияние планет в солярной карте'
          }, {
            icon: <BookOpen className="w-5 h-5" data-id="element-27240" />,
            text: 'Активные дома соляра'
          }, {
            icon: <Zap className="w-5 h-5" data-id="element-27241" />,
            text: 'Ключевые аспекты между планетами'
          }, {
            icon: <Briefcase className="w-5 h-5" data-id="element-27242" />,
            text: 'Прогноз по карьере и финансам'
          }, {
            icon: <Heart className="w-5 h-5" data-id="element-27243" />,
            text: 'Прогноз по отношениям'
          }, {
            icon: <TrendingUp className="w-5 h-5" data-id="element-27244" />,
            text: 'Личностный рост и развитие'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-27245" />,
            text: 'Зоны риска и предупреждения'
          }, {
            icon: <Compass className="w-5 h-5" data-id="element-27246" />,
            text: 'Рекомендации и советы на год'
          }].map((item, i) => <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-27247">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-27248">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-27249">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 6. ПРИМЕР ОТЧЁТА */}
      <AnimatedSection className="py-14 sm:py-24 bg-white" data-id="element-27250">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-27251">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl" data-id="element-27252">
            <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-27253">
              <div className="p-6 sm:p-10 lg:p-16 flex flex-col justify-center" data-id="element-27254">
                <span className="text-xs font-bold text-amber-300 uppercase tracking-[0.2em] mb-3 sm:mb-4" data-id="element-27255">
                  Пример отчёта
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 leading-tight" data-id="element-27256">
                  Детальный прогноз, а не общие гороскопы
                </h2>
                <p className="text-slate-400 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed" data-id="element-27257">
                  Каждый отчёт содержит графическую схему солярной карты,
                  подробный анализ всех планет и домов, а также помесячные
                  рекомендации.
                </p>
                <ul className="space-y-3 mb-6 sm:mb-10" data-id="element-27258">
                  {['Графическая схема солярной карты', 'Персональный прогноз по сферам жизни', 'Помесячные рекомендации', 'Профессиональное оформление'].map((t, i) => <li key={i} className="flex items-center text-slate-300 text-sm" data-id="element-27259">
                      <Check className="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" data-id="element-27260" />
                      {t}
                    </li>)}
                </ul>
                <Button size="lg" variant="outline" className="w-fit bg-white border-0 hover:bg-slate-100" onClick={() => onNavigate('report-preview-solar')} data-id="element-27261">
                  Посмотреть пример PDF
                </Button>
              </div>
              <div className="bg-slate-800/50 p-4 sm:p-8 lg:p-12 flex items-center justify-center" data-id="element-27262">
                <motion.div animate={{
                y: [0, -8, 0]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="w-full max-w-sm bg-white rounded-xl shadow-2xl p-8" data-id="element-27263">
                  <div className="w-full h-32 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg mb-6 flex items-center justify-center relative overflow-hidden" data-id="element-27264">
                    <div className="w-20 h-20 rounded-full border-2 border-amber-200 flex items-center justify-center" data-id="element-27265">
                      <Sun className="w-8 h-8 text-amber-400" data-id="element-27266" />
                    </div>
                    <Star className="w-4 h-4 text-orange-300 absolute top-4 right-6" data-id="element-27267" />
                    <Sparkles className="w-3 h-3 text-amber-300 absolute bottom-6 left-8" data-id="element-27268" />
                  </div>
                  <div className="w-3/4 h-3 bg-slate-200 rounded mb-3" data-id="element-27269" />
                  <div className="w-1/2 h-3 bg-slate-100 rounded mb-6" data-id="element-27270" />
                  {/* Mini chart bars */}
                  <div className="flex items-end gap-1.5 h-12 mb-6" data-id="element-27271">
                    {[60, 80, 45, 90, 70, 55, 85, 40, 75, 65, 50, 88].map((h, i) => <div key={i} className="flex-1 rounded-t bg-gradient-to-t from-amber-200 to-amber-100" style={{
                    height: `${h}%`
                  }} data-id="element-27272" />)}
                  </div>
                  <div className="space-y-2.5" data-id="element-27273">
                    <div className="w-full h-2 bg-slate-100 rounded" data-id="element-27274" />
                    <div className="w-full h-2 bg-slate-100 rounded" data-id="element-27275" />
                    <div className="w-5/6 h-2 bg-slate-50 rounded" data-id="element-27276" />
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 7. КОМУ ПОДХОДИТ СОЛЯР */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-27277">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-27278">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-8 sm:mb-12 text-center" data-id="element-27279">
            Кому подходит соляр
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-27280">
            {[{
            icon: <Eye className="w-5 h-5" data-id="element-27281" />,
            text: 'Людям, которые хотят понимать тенденции будущего года'
          }, {
            icon: <Target className="w-5 h-5" data-id="element-27282" />,
            text: 'Тем, кто планирует важные изменения в жизни'
          }, {
            icon: <Shield className="w-5 h-5" data-id="element-27283" />,
            text: 'Тем, кто хочет подготовиться к важным событиям'
          }, {
            icon: <Star className="w-5 h-5" data-id="element-27284" />,
            text: 'Людям, интересующимся астрологией и прогнозами'
          }, {
            icon: <Briefcase className="w-5 h-5" data-id="element-27285" />,
            text: 'Предпринимателям для планирования бизнес-решений'
          }, {
            icon: <Users className="w-5 h-5" data-id="element-27286" />,
            text: 'Тем, кто хочет выбрать лучшее время для начинаний'
          }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-white rounded-xl px-5 py-4 border border-slate-100" data-id="element-27287">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-27288">
                  {item.icon}
                </div>
                <span className="text-sm font-medium text-slate-700" data-id="element-27289">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 8. ПОЧЕМУ ВАЖНО ДЕЛАТЬ СОЛЯР КАЖДЫЙ ГОД */}
      <AnimatedSection className="py-12 sm:py-20 bg-white" data-id="element-27290">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-27291">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16" data-id="element-27292">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-27293">
              Почему важно делать соляр каждый год
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto" data-id="element-27294">
            {[{
            icon: <Compass className="w-6 h-6" data-id="element-27295" />,
            color: 'bg-amber-100 text-amber-600',
            title: 'Понимание главных тем года',
            desc: 'Каждый год приносит новые задачи и возможности. Соляр помогает увидеть, на чём сфокусироваться.'
          }, {
            icon: <Zap className="w-6 h-6" data-id="element-27296" />,
            color: 'bg-emerald-100 text-emerald-600',
            title: 'Использование благоприятных периодов',
            desc: 'Зная удачные месяцы, вы можете планировать важные решения, запуски и переговоры.'
          }, {
            icon: <Shield className="w-6 h-6" data-id="element-27297" />,
            color: 'bg-rose-100 text-rose-600',
            title: 'Осознание сложных этапов',
            desc: 'Предупреждён — значит вооружён. Вы будете готовы к непростым периодам и сможете минимизировать потери.'
          }, {
            icon: <Target className="w-6 h-6" data-id="element-27298" />,
            color: 'bg-indigo-100 text-indigo-600',
            title: 'Осознанное планирование',
            desc: 'Соляр даёт ориентиры для более осознанного подхода к планированию карьеры, отношений и личных целей.'
          }].map((item, i) => <Card key={i} padding="lg" className="border-slate-100" data-id="element-27299">
                <div className="flex items-start gap-5" data-id="element-27300">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-27301">
                    {item.icon}
                  </div>
                  <div data-id="element-27302">
                    <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-27303">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-27304">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 9. CTA */}
      <div ref={ctaRef} className="scroll-mt-10" data-id="element-27305">
        <AnimatedSection className="py-14 sm:py-24 bg-slate-900" data-id="element-27306">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-27307">
            <motion.div animate={{
            scale: [1, 1.15, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-27308">
              <Sun className="w-10 h-10 text-amber-400 mx-auto mb-4 sm:mb-6" data-id="element-27309" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6" data-id="element-27310">
              Рассчитайте свой солярный прогноз
            </h2>
            <p className="text-base sm:text-lg text-slate-400 mb-8 sm:mb-10 max-w-2xl mx-auto" data-id="element-27311">
              Введите данные рождения, чтобы получить персональный
              астрологический прогноз на следующий год вашей жизни.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-27312">
              <Button size="lg" onClick={scrollToPricing} className="text-base sm:text-lg px-10 shadow-lg shadow-amber-500/20 bg-amber-500 hover:bg-amber-600" data-id="element-27313">
                Построить соляр
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-27314" />
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-sm text-slate-500" data-id="element-27315">
              <span className="flex items-center" data-id="element-27316">
                <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-27317" /> Данные
                защищены
              </span>
              <span className="flex items-center" data-id="element-27318">
                <Clock className="w-4 h-4 mr-1.5 text-amber-400" data-id="element-27319" /> Результат
                мгновенно
              </span>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* 10. ТАРИФЫ */}
      <div ref={pricingRef} className="scroll-mt-10" data-id="element-27320">
        <ServicePurchaseSection serviceType="solar" onNavigate={onNavigate} data-id="element-27321" />
      </div>

      {/* FAQ */}
      <AnimatedSection className="py-12 sm:py-20 bg-slate-50" data-id="element-27322">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-27323">
          <div className="text-center mb-8 sm:mb-12" data-id="element-27324">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 sm:mb-4" data-id="element-27325">
              Частые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Чем соляр отличается от натальной карты?',
          content: 'Натальная карта описывает вашу личность в целом — характер, таланты, потенциал. Соляр — это прогноз на конкретный год жизни, от одного дня рождения до следующего. Натальная карта строится один раз, а соляр обновляется ежегодно.'
        }, {
          id: '2',
          title: 'Когда лучше всего делать соляр?',
          content: 'Идеально — за 1-2 месяца до дня рождения или сразу после него. Так вы получите прогноз на предстоящий год и сможете заранее подготовиться к важным периодам.'
        }, {
          id: '3',
          title: 'Нужно ли знать точное время рождения?',
          content: 'Да, для точного расчёта соляра необходимо знать время рождения. Оно влияет на положение домов в солярной карте, а значит — на прогноз по конкретным сферам жизни.'
        }, {
          id: '4',
          title: 'Чем базовый соляр отличается от Premium?',
          content: 'Базовый соляр содержит общий прогноз и ключевые тенденции года (1-3 страницы). Premium включает полный анализ всех планет и домов, помесячный прогноз, детальные рекомендации по карьере, отношениям и финансам (25-35 страниц).'
        }, {
          id: '5',
          title: 'Влияет ли место встречи дня рождения на соляр?',
          content: 'Да! Место, где вы встречаете день рождения, влияет на солярную карту. Некоторые астрологи рекомендуют специально выбирать город для встречи дня рождения, чтобы улучшить прогноз. В Premium отчёте мы даём рекомендации по этому вопросу.'
        }]} data-id="element-27326" />
        </div>
      </AnimatedSection>
    </div>;
}